import {Fragment, useMemo, useEffect, useContext} from 'react';
import {useTranslation} from 'react-i18next';

import {groupApi} from 'api';
import {PageHeader, Tab, Tabs} from 'components';
import {UserContext} from 'context';
import {useApiData} from 'hooks';
import {Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {userAuthUtils} from 'utils';

import {Activities} from './Activities';
import {AgentsAvailability} from './AgentsAvailability';
import {ChatGPT} from './ChatGPT';
import {Claude} from './Claude';
import {ClientSettings} from './ClientSettings';
import {DeepgramSettingsPage} from './DeepgramSettings';
import {InSession} from './InSession';
import {InternalLiveAgentSettingsPage} from './InternalLiveAgentSettings';
import {RequestAppointment} from './RequestAppointment';
import {SIPSettings} from './SIPSettings';
import {MastraSettingsPage} from './SIPSettings/MastraSettings';
import {WatsonxSettingsPage} from './SIPSettings/WatsonxSettings';
import {WhatsappSettingsPage} from './WhatsappSettings';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION;

export const AccountSession: React.FC = () => {
    const {t} = useTranslation();
    const {settings: {featuresAvailability}} = useContext(UserContext);
    const {send: fetchGroups, data: groups = []} = useApiData<Group[]>(groupApi.getGroups);

    const {
        inSession, requestAppointment, agentsAvailability, clientSettings, activities,
        chatGpt, claude, watsonx, mastra, sip, deepgram, whatsapp, internalLiveAgent
    } = useMemo(() => userAuthUtils.getUserPermissions().account.session, []);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    return (
        <Fragment>
            <PageHeader title={t(T_KEYS.TITLE)} description={t(T_KEYS.DESCRIPTION)} />
            <Tabs className='scrollbar-hide'>
                <Tab name={t(T_KEYS.IN_SESSION.TITLE)} hidden={!inSession?.enabled}>
                    <InSession />
                </Tab>
                <Tab name={t(T_KEYS.ACTIVITIES.TITLE)} hidden={!activities?.enabled || !featuresAvailability.activitiesManagement}>
                    <Activities />
                </Tab>
                <Tab name={t(T_KEYS.REQUEST_APPOINTMENT_SECTION.TITLE)} hidden={!requestAppointment?.enabled}>
                    <RequestAppointment groups={groups} />
                </Tab>
                <Tab name={t(T_KEYS.AGENTS_AVAILABILITY.TITLE)} hidden={!agentsAvailability?.enabled}>
                    <AgentsAvailability />
                </Tab>
                <Tab name={t(T_KEYS.CLIENT_SETTINGS.TITLE)} hidden={!clientSettings?.enabled}>
                    <ClientSettings />
                </Tab>
                <Tab name={t(T_KEYS.CHAT_GPT.TITLE)} hidden={!chatGpt?.enabled || !featuresAvailability.chatGPT}>
                    <ChatGPT />
                </Tab>
                <Tab name={t(T_KEYS.CLAUDE.TITLE)} hidden={!claude?.enabled || !featuresAvailability.claude}>
                    <Claude />
                </Tab>
                <Tab name={t(T_KEYS.WATSONX.TITLE)} hidden={!watsonx?.enabled || !featuresAvailability.watsonx}>
                    <WatsonxSettingsPage />
                </Tab>
                <Tab name={t(T_KEYS.MASTRA.TITLE)} hidden={!mastra?.enabled || !featuresAvailability.mastra}>
                    <MastraSettingsPage />
                </Tab>
                <Tab name={t(T_KEYS.SIP.TITLE)} hidden={!sip?.enabled || !featuresAvailability.sip}>
                    <SIPSettings />
                </Tab>
                <Tab name={t(T_KEYS.DEEPGRAM.TITLE)} hidden={!deepgram?.enabled || !featuresAvailability.deepgram}>
                    <DeepgramSettingsPage />
                </Tab>
                <Tab name={t(T_KEYS.WHATSAPP.TITLE)} hidden={!whatsapp?.enabled || !featuresAvailability.whatsapp}>
                    <WhatsappSettingsPage />
                </Tab>
                <Tab
                    name={t(T_KEYS.INTERNAL_LIVE_AGENT.TITLE)}
                    hidden={!internalLiveAgent?.enabled || !featuresAvailability.internalLiveAgent}
                >
                    <InternalLiveAgentSettingsPage />
                </Tab>
            </Tabs>
        </Fragment>
    );
};
